import { createReducer, on } from '@ngrx/store';
import { QuizProgress } from '../models';
import { quizProgressActions } from '../actions/quiz-progress.actions';

export interface SingleProgressState {
    progress: QuizProgress | null;
    loading: boolean;
    loaded: boolean | null;
    saving: boolean;
    retrying: boolean;
}

export type QuizProgressState = Record<string, SingleProgressState>;

const initialSingleState: SingleProgressState = {
    progress: null,
    loading: false,
    loaded: null,
    saving: false,
    retrying: false,
};

const initialState: QuizProgressState = {};

const getProgressState = (state: QuizProgressState, quizId: string): SingleProgressState => {
    return state[quizId] ?? { ...initialSingleState };
};

export const quizProgressReducer = createReducer(
    initialState,

    // Get Progress
    on(
        quizProgressActions.getProgress,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: { ...getProgressState(state, quizId), loading: true },
        }),
    ),
    on(
        quizProgressActions.getProgressSuccess,
        (state, { payload }): QuizProgressState => ({
            ...state,
            [payload.quizId]: {
                ...getProgressState(state, payload.quizId),
                progress: payload,
                loading: false,
                loaded: true,
            },
        }),
    ),
    on(
        quizProgressActions.getProgressNotStarted,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: {
                ...getProgressState(state, quizId),
                progress: null,
                loading: false,
                loaded: true,
            },
        }),
    ),
    on(
        quizProgressActions.getProgressError,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: {
                ...getProgressState(state, quizId),
                loading: false,
                loaded: false,
            },
        }),
    ),

    // Save Answer
    on(
        quizProgressActions.saveAnswer,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: { ...getProgressState(state, quizId), saving: true },
        }),
    ),
    on(
        quizProgressActions.saveAnswerSuccess,
        (state, { payload }): QuizProgressState => ({
            ...state,
            [payload.quizId]: {
                ...getProgressState(state, payload.quizId),
                progress: payload,
                loaded: true,
                saving: false,
            },
        }),
    ),
    on(
        quizProgressActions.saveAnswerError,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: { ...getProgressState(state, quizId), saving: false },
        }),
    ),

    // Retry
    on(
        quizProgressActions.retry,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: { ...getProgressState(state, quizId), retrying: true },
        }),
    ),
    on(quizProgressActions.retrySuccess, (state, { quizId }): QuizProgressState => {
        const { [quizId]: _, ...rest } = state;
        return rest;
    }),
    on(
        quizProgressActions.retryError,
        (state, { quizId }): QuizProgressState => ({
            ...state,
            [quizId]: { ...getProgressState(state, quizId), retrying: false },
        }),
    ),

    // Clear
    on(quizProgressActions.clear, (state, { quizId }): QuizProgressState => {
        const { [quizId]: _, ...rest } = state;
        return rest;
    }),
    on(quizProgressActions.clearAll, (): QuizProgressState => initialState),
);
